import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import withMountGuard from 'hocs/withMountGuard'
import fetchLoyaltyActionRewards from 'actions/loyalty/fetchLoyaltyActionRewards'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useTabVisibility from 'hooks/useTabVisibility'
import usePrevious from 'hooks/usePrevious'
import { LabRemoteConfigKeys } from 'constants/lab'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useLoyaltyActionRewardCelebration from 'loyalty/hooks/useLoyaltyActionRewardCelebration'
import useLoyaltyActionRewardPolling from 'loyalty/hooks/useLoyaltyActionRewardPolling'
import useLoyaltyActionRewards from 'loyalty/hooks/useLoyaltyActionRewards'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { fillArray } from 'lib/array/arrayUtils'
import { isRecord } from 'lib/object/objectUtils'
import { Fragment, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import LoyaltyActionRewardsListGrouped from './LoyaltyActionRewardsListGrouped'
import { LoyaltyActionRewardsListItemContentLoadingSkeleton } from './LoyaltyActionRewardsListItemContent'
import LoyaltyActionRewardCelebrationConfetti from './LoyaltyActionRewardCelebrationConfetti'
import LoyaltyActionRewardContext from './LoyaltyActionRewardContext'
import LoyaltyActionRewardsSummary, {
  LoyaltyActionRewardsSummaryLoadingSkeleton,
} from './LoyaltyActionRewardsSummary'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

const SKELETON_ROW_COUNT = 3
const SECTION_IN_VIEW_OBSERVER_OPTIONS: IntersectionObserverInit = { threshold: 0.2 }

function LoyaltyActionRewardsSectionLoadingSkeleton() {
  return (
    <VerticalSpacer gap={24}>
      <LoyaltyActionRewardsSummaryLoadingSkeleton />
      <div>
        {fillArray(SKELETON_ROW_COUNT).map(index => (
          <Fragment key={index}>
            <LoyaltyActionRewardsListItemContentLoadingSkeleton />
          </Fragment>
        ))}
      </div>
    </VerticalSpacer>
  )
}

function LoyaltyActionRewardsSection() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [tier] = useCurrentLoyaltyTier()
  // Showing celebration when section is in view
  const { celebrationActive, celebrationId } = useLoyaltyActionRewardCelebration()
  const [activeCelebrationId, setActiveCelebrationId] = useState<number>()

  const { pendingActionNames, startPolling } = useLoyaltyActionRewardPolling(celebrationId)
  const { rewards, isInitialLoading } = useLoyaltyActionRewards({ pendingActionNames })

  const isTabVisible = useTabVisibility()
  const wasTabVisible = usePrevious(isTabVisible, isTabVisible)
  const sectionRef = useRef<HTMLDivElement>(null)
  const [sectionInView, setSectionInView] = useState(false)

  useEffect(() => {
    // A tab return gets one fresh catalogue read. Modal submit polling is handled separately.
    const returnedToVisibleTab = !wasTabVisible && isTabVisible

    if (returnedToVisibleTab) {
      dispatch(fetchLoyaltyActionRewards({ force: true }))
    }
  }, [dispatch, isTabVisible, wasTabVisible])

  useEffect(() => {
    const section = sectionRef.current
    if (!section) return

    const observer = new IntersectionObserver(([entry]) => {
      setSectionInView(entry.isIntersecting)
    }, SECTION_IN_VIEW_OBSERVER_OPTIONS)
    observer.observe(section)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (celebrationActive && sectionInView) {
      setActiveCelebrationId(celebrationId)
    }
  }, [celebrationActive, celebrationId, sectionInView])

  const activeCelebration = activeCelebrationId === celebrationId && celebrationActive

  const shouldHide = !isInitialLoading && rewards.length === 0

  const programName = getLoyaltyProgramDisplayName()
  const subtitle = intl.formatMessage({
    id: 'Dv5YqkEUdwktAig5LpRV',
    defaultMessage: 'Complete quests to earn {programName} Points to use towards your next luxury escape.',
    description: 'Supporting body copy under the Société Quests heading on the member account benefits tab',
  }, {
    programName,
  })

  if (shouldHide) {
    return null
  }

  return (
    <LoyaltyActionRewardContext value={{ startPolling, pendingActionNames }}>
      <ProductPaletteProvider palette={`tier-${tier}`}>
        {activeCelebration && <LoyaltyActionRewardCelebrationConfetti key={celebrationId} tier={tier} />}
        <div ref={sectionRef}>
          <Sheet
            id="societe-quests"
            padding="24 0"
            tabletPadding="48 0"
            background="normal-background"
          >
            <LayoutContainer variant="medium">
              <VerticalSpacer gap={24}>
                <VerticalSpacer gap={2}>
                  <Heading variant="heading3" align="start">
                    <FormattedMessage
                      id="GtvyGEVhy4MKBPEZ9wdF"
                      defaultMessage="Your {programName} quests"
                      description="Main heading for the Société Quests module on the member account landing page"
                      values={{
                        programName,
                      }}
                    />
                  </Heading>
                  <BodyText variant="medium" align="start">
                    {subtitle}
                  </BodyText>
                </VerticalSpacer>
                {isInitialLoading && <LoyaltyActionRewardsSectionLoadingSkeleton />}
                {!isInitialLoading && (
                  <>
                    <LoyaltyActionRewardsSummary
                      rewards={rewards}
                      tier={tier}
                    />
                    <LoyaltyActionRewardsListGrouped
                      rewards={rewards}
                      tier={tier}
                    />
                  </>
                )}
              </VerticalSpacer>
            </LayoutContainer>
          </Sheet>
        </div>
      </ProductPaletteProvider>
    </LoyaltyActionRewardContext>
  )
}

export default withMountGuard(() => {
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const remoteConfig = useLabRemoteConfig(LabRemoteConfigKeys.loyaltyActionRewardsSection)
  const isSectionEnabled = !!remoteConfig
    && !remoteConfig.error
    && isRecord(remoteConfig.value)
    && remoteConfig.value.enabled === true

  return loyaltyEnabled && !!memberId && isSectionEnabled
})(LoyaltyActionRewardsSection)
